import { FC, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, Button, IconButton, Card, CardHeader, List, ListItem, Tooltip } from '@mui/material';
import { Grid, GridCellProps, GridRowProps, GridColumn, GridPageChangeEvent } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State as ODataState, SortDescriptor } from '@progress/kendo-data-query';
import TableHeader from '../../../TableHeader';
import TableHeaderSearch from '../../../controls/TableHeaderSearch';
import { gridColumnProperties } from 'src/types/gridColumnProperties';
import { Activity, ActivityType } from 'src/types/activity';
import crmApi from 'src/api/CrmApi';
import useAuth from 'src/hooks/useAuth';
import 'src/theme/partials/ActivityGrid.css';
import ActivityTypeCell from 'src/components/grid/ActivityTypeCell';
import ActivityCell from 'src/components/grid/ActivityCell';
import ActivityCustomerCell from 'src/components/grid/ActivityCustomerCell';
import useCrm from 'src/hooks/useCrm';
import DropdownFilter from 'src/components/controls/DropdownFilter';
import { Filter } from 'src/types/filter';
import DateCell from 'src/components/grid/DateCell';
import FilterOperators from 'src/components/grid/FilterOperators';
import ColumnMenu from 'src/components/grid/ColumnMenu';
import { useParams } from 'react-router-dom';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import PersonIcon from '@mui/icons-material/Person';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { CRMActivityEntity } from 'src/types/CRMActivityEntity';
import useUpdateEffect from 'src/hooks/useUpdateEffect';
import useSnackbar from 'src/hooks/useSnackbar';
import { UserSimple } from 'src/types/userSimple';
import useNotifications from 'src/hooks/useNotifications';
import DropdownLink from 'src/components/controls/DropdownLink';
import ListIcon from '@mui/icons-material/List';
import { GetFilterCollection } from './GetFilterCollection';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import MobileFilterDialog from 'src/components/grid/MobileFilterDialog';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import { SETTING_KEYS } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
import { FilterOption } from 'src/types/filterOption';
import { compositeToSelectedFilters } from 'src/utils/kendoGridHelpers';
import { getPageSizeSetting } from 'src/utils/settingsHelper';

interface Props {
  isOnDashboard?: boolean;
  defaultFilterByTask?: boolean;
  showCostomerColumn?: boolean;
  headerTitle?: string;
  className?: string;
}

const initialSort: Array<SortDescriptor> = [{ field: 'createdDate', dir: 'asc' }];

const ActivityGrid: FC<Props> = ({ isOnDashboard, headerTitle, showCostomerColumn, defaultFilterByTask, className }) => {
  const ActivityGridPageSizeKey = SETTING_KEYS.ActivityGridPageSize;
  const [activities, setActivities] = useState<Activity[]>([]);
  const [dataIsLoading, setDataIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  let { customerNumber } = useParams();
  const { openSnackBar } = useSnackbar();
  const { reloadTaskNotifications } = useNotifications();
  const [selectedFilterCollection, setSelectedFilterCollection] = useState<number>(0);
  const [expandAllActivities, setExpandAllActivities] = useState<boolean>(false);
  const {
    activityTypes,
    getAllActivityTypes,
    allCRMUsers,
    editActivityClicked,
    deleteActivityClicked,
    shouldReloadGrid,
    setShouldReloadGrid,
    activityFilters,
    setActivityFilters,
    setFilterBtnsVal,
    filterBtnsVal,
    filterBtnsValComplete,
    setFilterBtnsValComplete,
    isMyActivities,
    setIsMyActivities,
  } = useCrm();
  const { savePagingSettings, userSettings } = useSettings();
  const pageSize = getPageSizeSetting(userSettings, ActivityGridPageSizeKey, 50);
  const { user } = useAuth();

  let defaultFilters: CompositeFilterDescriptor[] = [];
  if (defaultFilterByTask) {
    defaultFilters.push({
      logic: 'and',
      filters: [{ field: 'activityType', operator: 'eq', value: 'Task' }],
    });
  }

  const [dropdownFilters, setDropdownFilters] = useState<Filter[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<{ [field: string]: FilterOption[] }>(compositeToSelectedFilters(activityFilters));

  const [gridDataState, setGridDataState] = useState<ODataState>({
    sort: initialSort,
    take: pageSize,
    skip: 0,
  });

  const exportGrid = useRef(null);
  const excelExport = () => {
    if (exportGrid.current !== null) {
      exportGrid.current.save();
    }
  };

  let otherColumns: gridColumnProperties[] = [];

  // if hasCustomerCol push customer column to columns
  if (showCostomerColumn) {
    otherColumns.push({
      field: 'customerName',
      title: 'CUSTOMER',
      cells: {
        data: (props: GridCellProps) => <ActivityCustomerCell {...props} />,
      },
      display: true,
    });
  }
  //Filters for the filter collections dropdown
  const ActivityFilterCollections = GetFilterCollection(user.id);

  function reloadGrid(gridState: ODataState) {
    setDataIsLoading(true);

    crmApi
      .getActivityList(gridState)
      .then((res) => {
        setActivities(res.value);
        setTotal(res['@odata.count']);
      })
      .catch((err) => {
        openSnackBar(`Error: ${err}`, 'error', 10000);
        console.error('Error: ', err);
      })
      .finally(() => {
        setDataIsLoading(false);
        setShouldReloadGrid(false);
      });
  }

  //Listen for the shouldReloadGrid flag and set to false when done. Currently triggers when user deletes an activity
  useEffect(() => {
    if (!gridDataState.filter) return;
    if (shouldReloadGrid) {
      reloadGrid(gridDataState);
    }
  }, [shouldReloadGrid]);

  useEffect(() => {
    if (activityTypes.length === 0) {
      getAllActivityTypes();
    }
    let arr = activityTypes.map((a: ActivityType) => ({ name: a.activityType, value: a.activityType }));
    const filters: Filter[] = [
      {
        title: 'Type',
        field: 'activityType',
        options: arr,
      },
      {
        title: 'Assigned To',
        field: 'assignedToUsername',
        options: allCRMUsers.map((u: UserSimple) => ({ name: u.username, value: u.username })),
      },
    ];

    setDropdownFilters(filters);
  }, [activityTypes]);

  // UPDATE FILTERS, GRID
  useUpdateEffect(() => {
    if (!gridDataState.filter) return;

    reloadGrid(gridDataState);
  }, [gridDataState]);

  const toggleTaskComplete = (activity: Activity) => {
    crmApi
      .getCrmActivity(activity.activityId)
      .then((crmActivity: CRMActivityEntity) => {
        crmActivity.isComplete = !crmActivity.isComplete;
        crmApi.upsertActivity(crmActivity).then(() => {
          reloadGrid(gridDataState);
          reloadTaskNotifications();
        });
      })
      .catch((err) => {
        openSnackBar(`Error: ${err}`, 'error', 10000);
        console.error('Error: ', err);
      });
  };

  const handleEditActivityForm = useCallback(
    (activity: Activity) => {
      editActivityClicked(activity.activityId);
    },
    [editActivityClicked]
  );

  const handleDeleteActivityForm = useCallback(
    (e: Activity) => {
      deleteActivityClicked(e.activityId);
    },
    [deleteActivityClicked]
  );

  /**
   * When the "other filters" dropdown changes
   * @param value the index of the selected filter
   */
  const onFilterCollectionChange: (value: number) => void = (value) => {
    setSelectedFilterCollection(value);
  };

  //run filters
  useEffect(() => {
    let filterList: (CompositeFilterDescriptor | FilterDescriptor)[] = [...activityFilters];

    //The "other filters" dropdown
    const filterCollectionFilters = selectedFilterCollection ? ActivityFilterCollections[selectedFilterCollection].value : null;
    if (filterCollectionFilters) {
      filterList.push(filterCollectionFilters);
    }
    //////////////////////////

    if (searchText && searchText.length > 0) {
      filterList.push({
        logic: 'or',
        filters: [
          { field: 'title', operator: 'contains', value: searchText },
          { field: 'content', operator: 'contains', value: searchText },
          { field: 'shipToNumber', operator: 'contains', value: searchText },
        ],
      });
    }

    // default to current customer
    if (isOnDashboard && customerNumber) {
      filterList.push({
        field: 'customerNumber',
        operator: 'eq',
        value: Number(customerNumber),
      });
    }

    if (filterBtnsVal.includes('pinned')) {
      filterList.push({
        field: 'isPinned',
        operator: 'eq',
        value: true,
      });
    }

    if (filterBtnsValComplete === 'complete') {
      filterList.push({
        field: 'isComplete',
        operator: 'eq',
        value: true,
      });
      filterList.push({
        field: 'activityType',
        operator: 'eq',
        value: 'Task',
      });
    } else if (filterBtnsValComplete === 'incomplete') {
      filterList.push({
        field: 'isComplete',
        operator: 'eq',
        value: false,
      });
      filterList.push({
        field: 'activityType',
        operator: 'eq',
        value: 'Task',
      });
    }

    // For isMyActivities logic, create a filter array
    let myActivitiesFilter: (CompositeFilterDescriptor | FilterDescriptor)[] = [
      {
        logic: 'or',
        filters: [
          {
            field: 'assignedTo',
            operator: 'eq',
            value: user.id,
          },
        ],
      },
    ];

    if (isMyActivities && !(searchText && searchText.length > 0) && !selectedFilterCollection) {
      //Pinned should always return first
      filterList = [
        {
          logic: 'or',
          filters: [
            {
              logic: 'and',
              filters: [
                {
                  field: 'isPinned',
                  operator: 'eq',
                  value: true,
                },
                ...myActivitiesFilter,
              ],
            },
            {
              logic: 'and',
              filters: myActivitiesFilter.concat(filterList),
            },
          ],
        },
      ];
    } else if (isMyActivities) {
      //If there is a search then don't include pinned logic
      filterList = myActivitiesFilter.concat(filterList);
    }

    //Always sort by pinned if isMyActivities
    let sort: SortDescriptor[] = isMyActivities
      ? [
          { field: 'isPinned', dir: 'desc' },
          { field: 'createdDate', dir: 'desc' },
        ]
      : [{ field: 'createdDate', dir: 'desc' }];

    const newDataState: ODataState = {
      ...gridDataState,
      filter: {
        filters: filterList,
        logic: 'and',
      },
      skip: 0,
      take: pageSize,
      sort: sort,
    };

    // check if newDataState equals current gridDataState, if they are the same to not re-render or update state
    if (JSON.stringify(newDataState) === JSON.stringify(gridDataState)) {
      return;
    }

    setGridDataState(newDataState);
  }, [filterBtnsVal, filterBtnsValComplete, isMyActivities, defaultFilterByTask, activityFilters, customerNumber, searchText, selectedFilterCollection]);

  const handleRowRender = (row: any, props: GridRowProps) => {
    const isPinned = props.dataItem && props.dataItem?.isPinned;
    return (
      <tr onDoubleClick={() => handleEditActivityForm(props.dataItem)} className={isPinned ? 'activity-grid__row is-pinned' : 'activity-grid__row'}>
        {row.props.children}
      </tr>
    );
  };

  const onDropdownFilterUpdated = (fullDescriptor: CompositeFilterDescriptor[]) => {
    setActivityFilters(fullDescriptor);

    const isTask = (data) => data.some((obj) => obj.filters.some((filter) => filter.field === 'activityType' && filter.operator === 'eq' && filter.value === 'Task'));

    if (!isTask(fullDescriptor)) {
      if (filterBtnsValComplete !== '') setFilterBtnsValComplete('');
    }
  };

  const handleMineAllChange = (e: any, val) => {
    if (val !== null) {
      setIsMyActivities(val);
    }
  };

  const handleFilterBtnChange = (e: any, val) => {
    setFilterBtnsVal(val);
  };
  const handleFilterBtnChangeComplete = (e: any, val) => {
    setFilterBtnsValComplete(val);
  };

  const handleHeaderSearch = (val: string) => {
    setSearchText(val);
  };

  const handlePageChange = (e: GridPageChangeEvent) => {
    const newPageSize = e.page.take;
    setGridDataState((prevState) => ({
      ...prevState,
      skip: e.page.skip,
      take: newPageSize,
    }));

    savePagingSettings(newPageSize, ActivityGridPageSizeKey);
  };

  const activityCell = useMemo(
    () => (cellProps: GridCellProps) => (
      <ActivityCell {...cellProps} onEdit={handleEditActivityForm} onDelete={handleDeleteActivityForm} searchText={searchText} expandAllActivities={expandAllActivities} />
    ),
    [searchText, handleEditActivityForm, handleDeleteActivityForm, expandAllActivities]
  );

  const dateCell = useMemo(() => (cellProps: GridCellProps) => <DateCell {...cellProps} dateOnly={false} />, []);

  return (
    <div className={className}>
      {headerTitle ? (
        <Box>
          <h3>Activity</h3>
        </Box>
      ) : (
        <></>
      )}
      <Card>
        <CardHeader
          className="table-header-container"
          title={
            <TableHeader
              hasProgress={activities && dataIsLoading}
              className="open-docs-table"
              hideColumnSelector={true}
              endContent={
                <div>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button variant="text" onClick={excelExport} sx={{ pr: 0 }}>
                      <Tooltip title="Export to Excel">
                        <DownloadIcon />
                      </Tooltip>
                    </Button>
                  </Box>
                </div>
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box className="toggle__mine-all__container">
                  <ToggleButtonGroup className="toggle__mine-all" value={isMyActivities} exclusive onChange={handleMineAllChange}>
                    <ToggleButton className="toggle__mine-all__btn" value={false}>
                      <Tooltip title="All Activities">
                        <PeopleAltIcon />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton className="toggle__mine-all__btn" value={true}>
                      <Tooltip title="My Activities">
                        <PersonIcon />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box className="table-header-content-group__search">
                  <TableHeaderSearch onEnter={(val) => handleHeaderSearch(val)} label="Search..." />
                </Box>
                {/* Mobile Filters */}
                <Box className="d-block d-md-none">
                  <MobileFilterDialog title="Filters">
                    <List>
                      <ListItem>
                        <ToggleButtonGroup value={filterBtnsVal} onChange={handleFilterBtnChange}>
                          <ToggleButton value="pinned" className="toggle__mine-all__btn">
                            Pinned
                          </ToggleButton>
                          <ToggleButton value="" onClick={() => setExpandAllActivities(!expandAllActivities)}>
                            {expandAllActivities ? 'Collapse All' : 'Expand All'}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </ListItem>
                      <ListItem>
                        <ToggleButtonGroup exclusive value={filterBtnsValComplete} onChange={handleFilterBtnChangeComplete}>
                          <ToggleButton value="incomplete" className="toggle__mine-all__btn">
                            Incomplete
                          </ToggleButton>
                          <ToggleButton value="complete" className="toggle__mine-all__btn">
                            Complete
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </ListItem>
                      <ListItem>
                        {dropdownFilters.length > 0 && (
                          <DropdownFilter
                            filters={dropdownFilters}
                            selectedFilters={selectedFilters}
                            onSelectedFiltersChange={setSelectedFilters}
                            onFilterUpdated={onDropdownFilterUpdated}
                            filterLogic="contains"
                          />
                        )}
                      </ListItem>
                      <ListItem>
                        <DropdownLink
                          options={ActivityFilterCollections}
                          startIcon={<ListIcon sx={{ fontSize: '2px' }} />}
                          defaultText="Filter Collections"
                          onChange={onFilterCollectionChange}
                        />
                      </ListItem>
                    </List>
                  </MobileFilterDialog>
                </Box>
                {/* Desktop Filters */}
                <Box className="d-none d-md-flex">
                  <Box className="table-header-toggle-btn-group">
                    <ToggleButtonGroup value={filterBtnsVal} onChange={handleFilterBtnChange}>
                      <ToggleButton value="pinned" className="toggle__mine-all__btn">
                        Pinned
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup exclusive value={filterBtnsValComplete} onChange={handleFilterBtnChangeComplete}>
                      <ToggleButton value="incomplete" className="toggle__mine-all__btn">
                        Incomplete
                      </ToggleButton>
                      <ToggleButton value="complete" className="toggle__mine-all__btn">
                        Complete
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ display: 'flex' }} className="table-header-content-group__filters">
                    {dropdownFilters.length > 0 && (
                      <DropdownFilter
                        filters={dropdownFilters}
                        selectedFilters={selectedFilters}
                        onSelectedFiltersChange={setSelectedFilters}
                        onFilterUpdated={onDropdownFilterUpdated}
                        filterLogic="contains"
                      />
                    )}
                  </Box>
                  <Box>
                    <DropdownLink
                      options={ActivityFilterCollections}
                      startIcon={<ListIcon sx={{ fontSize: '2px' }} />}
                      defaultText="Filter Collections"
                      onChange={onFilterCollectionChange}
                    />
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => setExpandAllActivities(!expandAllActivities)}>
                      <Tooltip title={expandAllActivities ? 'Collapse All' : 'Expand All'}>
                        {expandAllActivities ? <IndeterminateCheckBoxIcon color="action" /> : <LibraryAddIcon color="action" />}
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </TableHeader>
          }
        />
        <Box className="activity-grid">
          <ExcelExport data={activities} ref={exportGrid} fileName={'Activities_' + moment().format('YYYY-MM-DD')}>
            <ExcelExportColumn field="activityId" title="ID" width={50} />
            <ExcelExportColumn field="isPinned" title="PINNED" width={60} />
            <ExcelExportColumn field="content" title="CONTENT" width={350} />
            <ExcelExportColumn field="customerName" title="CUSTOMER NAME" />
            <ExcelExportColumn field="customerNumber" title="CUSTOMER No" width={80} />
            <ExcelExportColumn field="shipToNumber" title="SHIPTO" width={100} />
            <ExcelExportColumn field="assignedToUsername" title="ASSIGNED TO" width={200} />
            <ExcelExportColumn field="createdByUsername" title="CREATED BY USER" width={200} />
            <ExcelExportColumn field="followUpDate" title="FOLLOWUP" />
            {/* Task Oriented */}
            <ExcelExportColumn field="isComplete" title="COMPLETE" cellOptions={{ background: '#fffd9c' }} width={90} />
            {/* Call Oriented */}
            <ExcelExportColumn field="callOutcome" title="CALL OUTCOME" cellOptions={{ background: '#9cffdb' }} width={115} />
            <ExcelExportColumn field="callDirection" title="CALL DIRECTION" cellOptions={{ background: '#9cffdb' }} width={120} />
            <ExcelExportColumn field="didRespond" title="RESPONDED" cellOptions={{ background: '#9cffdb' }} width={85} />
            {/* Meeting Oriented */}
            <ExcelExportColumn field="location" title="LOCATION" cellOptions={{ background: '#a79cff' }} />
            <ExcelExportColumn field="attendees" title="ATTENDEES" cellOptions={{ background: '#a79cff' }} width={100} />
            <ExcelExportColumn field="programsPitched" title="PROGRAMS PITCHED" cellOptions={{ background: '#a79cff' }} width={140} />
            <Grid
              data={activities}
              className={showCostomerColumn ? 'has-customer-column' : ''}
              //rowRender={handleRowRender} //todo add back
              pageable={{
                buttonCount: 3,
                pageSizes: [20, 50, 100, 500],
                pageSizeValue: pageSize,
              }}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              total={total}
              sortable={true}
              scrollable="scrollable"
              {...gridDataState}
              dataItemKey="activityId"
              onDataStateChange={(e) => setGridDataState(e.dataState)}
              columnMenu={ColumnMenu}
              filterOperators={FilterOperators}
            >
              <GridColumn
                key="activityType"
                field="activityType"
                title="TYPE"
                cells={{
                  data: (props) => <ActivityTypeCell {...props} toggleTaskComplete={toggleTaskComplete} />,
                }}
                filterable={false}
              />
              <GridColumn key="title" field="title" title="TITLE" cells={{ data: activityCell }} filterable={false} />
              <GridColumn key="dueDate" field="dueDate" title="DUE" filter="date" className="activity-grid__due-cell" format="MMMM d yyy" cells={{ data: dateCell }} />
              <GridColumn
                key="createdDate"
                field="createdDate"
                title="CREATED"
                filter="date"
                className="activity-grid__created-cell"
                format="M/dd/yyyy h:mm a"
                cells={{ data: dateCell }}
              />
              {otherColumns.map((column, index) => {
                return <GridColumn key={index} {...column} width={230} />;
              })}
            </Grid>
          </ExcelExport>
        </Box>
      </Card>
    </div>
  );
};

export default ActivityGrid;
